import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';


// ═══════════════════════════════════════════════════════════════════
// Database Backup & Restore
//   - Admin / Superintendent  → Download full database
//   - Principal               → Download campus-only backup
//   - Admin only              → Restore from .db file
// ═══════════════════════════════════════════════════════════════════
function DatabaseBackupSection() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const isSuper = currentUser?.role === 'superintendent';
  const isPrincipal = currentUser?.role === 'principal';

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const loadInfo = () => {
    setLoading(true);
    setError('');
    api.get('/database/info')
      .then(r => setInfo(r.data))
      .catch(err => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadInfo(); }, []);

  const downloadViaFetch = async (url, fallbackName) => {
    const token = localStorage.getItem('token');
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      let detail = `HTTP ${res.status}`;
      try { detail = await res.text() || detail; } catch {}
      throw new Error(detail);
    }
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename="?([^"]+)"?/);
    a.download = m ? m[1] : fallbackName;
    a.click();
    URL.revokeObjectURL(objUrl);
  };

  const handleDownloadFull = async () => {
    setDownloading(true);
    setError('');
    try {
      await downloadViaFetch(
        '/api/database/download',
        `schoolsphere_full_backup_${Date.now()}.db`
      );
    } catch (err) {
      setError('Download failed: ' + err.message);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadCampus = async () => {
    if (!info?.campus_id) {
      setError('Your account is not assigned to a campus.');
      return;
    }
    setDownloading(true);
    setError('');
    try {
      await downloadViaFetch(
        `/api/database/download/campus/${info.campus_id}`,
        `campus_backup_${Date.now()}.db`
      );
    } catch (err) {
      setError('Download failed: ' + err.message);
    } finally {
      setDownloading(false);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setResult(null);
    setError('');
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.db')) {
      setError('Please select a .db file');
      return;
    }
    setRestoreFile(f);
  };

  const handleRestore = async () => {
    if (!restoreFile) return;
    setRestoring(true);
    setError('');
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', restoreFile);
      const res = await fetch('/api/database/restore', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setResult(data);
      setShowConfirm(false);
      setConfirmText('');
      setRestoreFile(null);
      loadInfo();
    } catch (err) {
      setError('Restore failed: ' + err.message);
      setShowConfirm(false);
    } finally {
      setRestoring(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow mt-6 text-gray-500 text-sm">
        <i className="fas fa-spinner fa-spin mr-2"></i>
        Checking database…
      </div>
    );
  }

  const canDownloadFull = isAdmin || isSuper;
  const canDownloadCampus = isPrincipal && !!info?.campus_id;
  const canRestore = isAdmin && info?.can_restore === true;
  const showDownloadCard = canDownloadFull || canDownloadCampus;

  return (
    <div className="bg-white p-6 rounded-lg shadow mt-6 border-t-4 border-purple-500">
      <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
        <i className="fas fa-database text-purple-600"></i>
        Database Backup
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        {canDownloadFull
          ? 'Download a full snapshot of your entire database.'
          : canDownloadCampus
          ? 'Download a snapshot of your campus data only.'
          : 'Backup options for your account.'}
      </p>

      {/* Current DB info */}
      {info && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4 text-sm">
          <div className="bg-gray-50 p-3 rounded">
            <p className="text-xs text-gray-500 mb-0.5">Engine</p>
            <p className="font-mono font-semibold">{info.engine}</p>
          </div>
          <div className="bg-gray-50 p-3 rounded">
            <p className="text-xs text-gray-500 mb-0.5">
              {canDownloadFull ? 'Full DB Size' : 'Size'}
            </p>
            <p className="font-mono font-semibold">{info.size_human}</p>
          </div>
          <div className="bg-gray-50 p-3 rounded col-span-2 md:col-span-1">
            <p className="text-xs text-gray-500 mb-0.5">Scope</p>
            <p className="font-mono font-semibold">
              {canDownloadFull ? 'All campuses' : canDownloadCampus ? 'Your campus only' : '—'}
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 text-red-700 p-3 rounded text-sm mb-4 border border-red-200">
          <i className="fas fa-exclamation-circle mr-1"></i> {error}
        </div>
      )}

      {result && (
        <div className="bg-green-50 border border-green-200 p-4 rounded mb-4">
          <p className="font-semibold text-green-800 mb-2">
            <i className="fas fa-check-circle mr-1"></i> Restore Complete
          </p>
          <p className="text-sm text-green-700 mb-2">{result.message}</p>
          <div className="text-xs text-green-700 bg-white p-2 rounded border border-green-200 space-y-1">
            <p><strong>Users in restored DB:</strong> {result.users_in_restored_db}</p>
            <p><strong>Backup of previous:</strong> <span className="font-mono">{result.backup_of_previous || '—'}</span></p>
            <p><strong>Backup folder:</strong> <span className="font-mono">{result.backup_dir}</span></p>
          </div>
        </div>
      )}

      <div className={`grid gap-4 ${showDownloadCard && canRestore ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
        {/* ─── Download — role-dependent ─── */}
        {showDownloadCard && (
          <div className="border rounded-lg p-4 bg-green-50 border-green-200">
            <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
              <i className="fas fa-download"></i>
              {canDownloadFull ? 'Download Full Database' : 'Download Campus Backup'}
            </h4>
            <p className="text-xs text-green-800 mb-3">
              {canDownloadFull
                ? 'Contains every campus, user, and record.'
                : 'Contains only your campus — classes, students, staff, attendance, fees. No other campus data is included.'}
            </p>
            <button
              onClick={canDownloadFull ? handleDownloadFull : handleDownloadCampus}
              disabled={downloading || (canDownloadCampus && !info?.campus_id)}
              className="w-full bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 text-sm"
            >
              {downloading ? (
                <><i className="fas fa-spinner fa-spin mr-1"></i> Preparing…</>
              ) : (
                <><i className="fas fa-download mr-1"></i> Download Now</>
              )}
            </button>
          </div>
        )}

        {/* ─── Restore — admin only ─── */}
        {canRestore && (
          <div className="border rounded-lg p-4 bg-red-50 border-red-200">
            <h4 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
              <i className="fas fa-upload"></i> Restore from Backup
              <span className="text-[10px] bg-red-600 text-white px-1.5 py-0.5 rounded uppercase">
                Admin
              </span>
            </h4>
            <p className="text-xs text-red-800 mb-3">
              Upload a previous <strong>full</strong> .db backup to replace the current database.
              Campus-only backups cannot be restored.
            </p>
            <input
              type="file"
              accept=".db"
              id="restore-db-input"
              onChange={handleFileChange}
              className="hidden"
            />
            <div className="flex gap-2">
              <button
                onClick={() => document.getElementById('restore-db-input').click()}
                className="flex-1 bg-white text-gray-700 border border-gray-300 px-3 py-2 rounded hover:bg-gray-50 text-sm truncate"
              >
                <i className="fas fa-folder-open mr-1"></i>
                {restoreFile ? restoreFile.name.slice(0, 18) : 'Choose .db file'}
              </button>
              <button
                onClick={() => setShowConfirm(true)}
                disabled={!restoreFile}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50 text-sm"
              >
                <i className="fas fa-upload mr-1"></i> Restore
              </button>
            </div>
            <p className="text-[11px] text-red-700 mt-2 italic">
              <i className="fas fa-exclamation-triangle mr-1"></i>
              A backup of the current DB is auto-saved to <code className="bg-white px-1 rounded">backups/pre_restore_*.db</code> first.
            </p>
          </div>
        )}
      </div>

      {/* Confirmation modal */}
      {showConfirm && restoreFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-5 border-b bg-red-50">
              <h3 className="text-lg font-bold text-red-900 flex items-center gap-2">
                <i className="fas fa-exclamation-triangle"></i>
                Confirm Database Restore
              </h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-800">
                You are about to <strong>replace the entire database</strong> with:
              </p>
              <div className="bg-gray-100 p-3 rounded text-xs font-mono">
                {restoreFile.name} ({(restoreFile.size / 1024 / 1024).toFixed(2)} MB)
              </div>
              <div className="bg-red-50 border border-red-200 p-3 rounded text-sm text-red-800">
                <p className="font-semibold mb-1">This will:</p>
                <ul className="list-disc list-inside text-xs space-y-0.5">
                  <li>Delete all current data (users, students, fees, attendance, everything)</li>
                  <li>Replace it with the uploaded file's data</li>
                  <li>Log everyone out (passwords come from the old DB)</li>
                  <li>Require a backend restart</li>
                </ul>
                <p className="text-xs mt-2">
                  <strong>Safety net:</strong> Your current DB is backed up to
                  <code className="bg-white px-1 rounded ml-1">backups/pre_restore_*.db</code> before replacement.
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Type <span className="text-red-600 font-mono">RESTORE</span> to confirm
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value.toUpperCase())}
                  className="w-full p-2 border rounded font-mono"
                  placeholder="RESTORE"
                  autoFocus
                />
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
              <button
                onClick={() => { setShowConfirm(false); setConfirmText(''); }}
                disabled={restoring}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleRestore}
                disabled={confirmText !== 'RESTORE' || restoring}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {restoring ? (
                  <><i className="fas fa-spinner fa-spin mr-1"></i> Restoring…</>
                ) : (
                  <><i className="fas fa-check mr-1"></i> Yes, Restore</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Main Bulk Upload component
// ═══════════════════════════════════════════════════════════════════
export default function BulkUpload() {
  const { user: currentUser } = useAuth();
  const isPrincipal = currentUser?.role === 'principal';
  const [file, setFile] = useState(null);
  const [uploadType, setUploadType] = useState('teachers');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      const validTypes = [
        'text/csv',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
      ];
      if (
        validTypes.includes(selected.type) ||
        selected.name.endsWith('.csv') ||
        selected.name.endsWith('.xlsx') ||
        selected.name.endsWith('.xls')
      ) {
        setFile(selected);
        setResult(null);
      } else {
        alert('Please upload a CSV or Excel file (.csv, .xlsx, .xls)');
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      alert('Please select a file first');
      return;
    }

    setLoading(true);
    setResult(null);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const endpoint = uploadType === 'teachers' ? '/bulk/teachers' : '/bulk/students';
      const res = await api.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      setFile(null);
      const input = document.getElementById('file-input');
      if (input) input.value = '';
    } catch (err) {
      setResult({
        success: false,
        errors: [err.response?.data?.detail || 'Upload failed'],
        created: 0,
        total: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async (type) => {
    try {
      const res = await api.get(`/bulk/template/${type}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_template.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Failed to download template');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const dropped = e.dataTransfer.files[0];
      if (
        dropped.name.endsWith('.csv') ||
        dropped.name.endsWith('.xlsx') ||
        dropped.name.endsWith('.xls')
      ) {
        setFile(dropped);
        setResult(null);
      } else {
        alert('Please drop a CSV or Excel file');
      }
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <h2 className="text-xl font-bold mb-4">📤 Bulk Upload</h2>

      {isPrincipal && (
        <div className="bg-blue-50 border border-blue-200 p-3 rounded mb-4">
          <p className="text-sm text-blue-700">
            <i className="fas fa-info-circle"></i>{' '}
            <strong>Campus Restriction:</strong> All uploaded teachers and students
            will be automatically assigned to your campus. Make sure the classes
            in your CSV belong to your campus.
          </p>
        </div>
      )}

      {!isPrincipal && (
        <div className="bg-yellow-50 border border-yellow-200 p-3 rounded mb-4">
          <p className="text-sm text-yellow-700">
            <i className="fas fa-exclamation-triangle"></i>{' '}
            <strong>Multi-Campus Upload:</strong> Make sure the class names
            in your CSV belong to the correct campus. Verify with the campus admin.
          </p>
        </div>
      )}

      <p className="text-sm text-gray-500 mb-4">
        Upload multiple {uploadType === 'teachers' ? 'teachers' : 'students'} at once using CSV or Excel file.
        <br />
        <span className="text-blue-600">📋 Tip: Download the template first to see the required format.</span>

        {uploadType === 'teachers' && (
          <span className="block text-xs text-gray-400 mt-1">
            Required columns: <code>first_name, last_name, email, password, phone, address, cnic, class_name</code>
          </span>
        )}

        {uploadType === 'students' && (
          <span className="block text-xs text-gray-400 mt-1">
            Required columns: <code>first_name, last_name, registration_number, class_name, date_of_birth, gender, admission_date, guardian_name, guardian_phone, guardian_email, guardian_cnic</code>
            <br />
            <span className="text-blue-500">
              💡 <strong>guardian_cnic</strong> uniquely identifies the parent — siblings
              with the same CNIC will be linked to the same parent account automatically.
            </span>
          </span>
        )}
      </p>

      {/* Upload Type Selector */}
      <div className="flex gap-4 mb-4">
        <button
          onClick={() => { setUploadType('teachers'); setResult(null); setFile(null); }}
          className={`px-4 py-2 rounded ${uploadType === 'teachers' ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
        >
          👨‍🏫 Teachers
        </button>
        <button
          onClick={() => { setUploadType('students'); setResult(null); setFile(null); }}
          className={`px-4 py-2 rounded ${uploadType === 'students' ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
        >
          👨‍🎓 Students
        </button>
      </div>

      {/* Template Download */}
      <div className="mb-4">
        <button
          onClick={() => downloadTemplate(uploadType)}
          className="text-blue-600 hover:text-blue-800 text-sm"
        >
          <i className="fas fa-download mr-1"></i> Download {uploadType} Template (CSV)
        </button>
      </div>

      {/* File Drop Area */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-8 text-center ${
          dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
        }`}
      >
        <i className="fas fa-cloud-upload-alt text-4xl text-gray-400 mb-3"></i>
        <p className="text-gray-500">
          {file ? (
            <>
              <span className="text-green-600 font-semibold">📄 {file.name}</span>
              <br />
              <span className="text-xs text-gray-400">
                ({(file.size / 1024).toFixed(1)} KB)
              </span>
            </>
          ) : (
            'Drag & drop your CSV or Excel file here, or click to browse'
          )}
        </p>
        <input
          id="file-input"
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileChange}
          className="hidden"
        />
        <button
          onClick={() => document.getElementById('file-input').click()}
          className="mt-3 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 text-sm"
        >
          Browse Files
        </button>
      </div>

      {/* Upload Button */}
      <button
        onClick={handleUpload}
        disabled={!file || loading}
        className={`mt-4 px-6 py-2 rounded text-white ${
          !file || loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
        }`}
      >
        {loading ? '⏳ Uploading...' : '🚀 Upload & Process'}
      </button>

      {/* Results */}
      {result && (
        <div className="mt-4 p-4 rounded-lg border">
          <h4 className="font-semibold mb-2">Upload Results</h4>

          <div className="text-sm flex flex-wrap gap-3">
            <span className="text-green-600">✅ Created: {result.created || 0}</span>

            {result.parents_created !== undefined && (
              <>
                <span className="text-blue-600">👨‍👩‍👧 New Parents: {result.parents_created}</span>
                <span className="text-purple-600">🔗 Parents Reused: {result.parents_reused}</span>
              </>
            )}

            <span className="text-gray-500">Total: {result.total || 0}</span>

            {result.errors && result.errors.length > 0 && (
              <span className="text-red-600">❌ Errors: {result.errors.length}</span>
            )}
          </div>

          {result.errors && result.errors.length > 0 && (
            <div className="mt-3 max-h-40 overflow-y-auto bg-red-50 p-3 rounded text-sm text-red-600">
              <strong>Errors:</strong>
              <ul className="list-disc pl-4 mt-1">
                {result.errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          {result.students && result.students.length > 0 && (
            <div className="mt-3 text-sm text-gray-600 bg-green-50 p-3 rounded">
              <strong>✅ Students Created ({result.students.length}):</strong>
              <div className="mt-1">{result.students.join(', ')}</div>
            </div>
          )}

          {result.teachers && result.teachers.length > 0 && (
            <div className="mt-3 text-sm text-gray-600 bg-green-50 p-3 rounded">
              <strong>✅ Teachers Created ({result.teachers.length}):</strong>
              <div className="mt-1">{result.teachers.join(', ')}</div>
            </div>
          )}
        </div>
      )}

      {/* ═══ Database Backup section ═══ */}
      <DatabaseBackupSection />
    </div>
  );
}